import React, { useState } from 'react';
import { Message } from 'talkplus-sdk/dist/model/types';
import Modal from '@common/modal';
import CloseButton from '@common/closeButton';
import Button from '@common/button';
import Text from '@common/text';
import Icon from '@common/assets';
import RadioInput from '@common/radioInput';
import { useChatToast } from '@components/chat/common/chatToast';
import { useChatChannelMessagesReportByChannelIdAndMessageId } from '@repository/chat/useChat';
import { FontStyle } from '@domain/constant/fontStyle';
import Colors from '@domain/constant/colors';
import { ReportEnum, reportTextMap, ReportType } from '@domain/constant/chat/ReportType';
import classnames from 'classnames/bind';
import styles from './index.module.scss';

const cx = classnames.bind(styles);

interface ChatReportModalProps {
  message: Message | null;
  visible: boolean;
  onClose: () => void;
}

const ChatReportModal = ({ message, visible, onClose }: ChatReportModalProps) => {
  const [reportType, setReportType] = useState<ReportType | null>(null);
  const [reason, setReason] = useState<string>('');
  const ChatToast = useChatToast();

  const getIsChecked = (type: ReportType) => reportType === type;
  const getIsDisabled = () => {
    if (!reportType) return true;
    if (reportType === ReportEnum.OTHER_REASON) {
      return reason.length < 10;
    }
    return false;
  };

  const { mutateAsync, isLoading } = useChatChannelMessagesReportByChannelIdAndMessageId();

  const username = message?.username;
  const textLength = message?.text?.length || 0;
  const text = textLength >= 200 ? `${message?.text?.slice(0, 200)}...` : message?.text;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isLoading) return;
    if (!reportType) return;
    if (!message) return;
    onClose();

    ChatToast({
      bottom: 72,
      content: '신고가 완료되었어요.',
    });

    await mutateAsync({
      channelId: message.channelId,
      messageId: message.id,
      rq: {
        type: reportType,
        reason: reportType === ReportEnum.OTHER_REASON ? reason : '',
      },
    });
  };

  const handleOnChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (e.target.value.length > 50) return;
    setReason(e.target.value);
  };

  const getEls = () => {
    const reportTypes = Object.keys(ReportEnum) as ReportType[];
    return reportTypes.map((type) => {
      if (type === ReportEnum.OTHER_REASON) {
        return (
          <label key={type} className={cx('itemArea', 'text')}>
            <div className={cx('radioArea')}>
              <RadioInput
                label={''}
                value={type}
                onChange={() => setReportType(type)}
                checked={getIsChecked(type)}
                name={'type'}
              />
              <Text fontStyle={FontStyle.B3_M} color={Colors.C_COOL_GRAY_90}>
                {reportTextMap[type]}
              </Text>
            </div>
            {reportType === ReportEnum.OTHER_REASON && (
              <div className={cx('descArea')}>
                <textarea
                  className={cx('textarea')}
                  rows={2}
                  maxLength={50}
                  placeholder={'구체적으로 작성해주시면 더 빠르게 조치할 수 있어요.'}
                  value={reason}
                  onChange={handleOnChange}
                />
                <Text fontStyle={FontStyle.B4_M} color={Colors.C_COOL_GRAY_70}>
                  최대 글자수 : {reason.length} / 50
                </Text>
              </div>
            )}
          </label>
        );
      }

      return (
        <label key={type} className={cx('itemArea')}>
          <RadioInput
            label={''}
            value={type}
            onChange={() => setReportType(type)}
            checked={getIsChecked(type)}
            name={'type'}
          />
          <Text fontStyle={FontStyle.B3_M} color={Colors.C_COOL_GRAY_90}>
            {reportTextMap[type]}
          </Text>
        </label>
      );
    });
  };

  return (
    <Modal visible={visible} dimmed>
      <form className={cx('modalArea')} onSubmit={handleSubmit}>
        <Text fontStyle={FontStyle.H4_B} color={Colors.C_JOBDA_BLACK}>
          신고하기
        </Text>

        <div className={cx('infoArea')}>
          <Text fontStyle={FontStyle.B2P_M} color={Colors.C_COOL_GRAY_70}>
            다음 메시지를 정말 신고하시겠어요?
          </Text>
          <Text fontStyle={FontStyle.B2P_M} color={Colors.C_COOL_GRAY_70}>
            무분별한 신고 시 제재를 받을 수 있어요.
          </Text>
        </div>

        <div className={cx('linkArea')}>
          <a
            className={cx('link')}
            href={'https://support-jobda.notion.site/911692beb82943d19201a5ba3937fa91?pvs=4'}
            target={'_blank'}
          >
            <Text fontStyle={FontStyle.B3_M} color={Colors.C_BLUE_100}>
              채팅 운영정책
            </Text>
            <Icon name={'arrowRightLight'} width={24} height={24} fill={Colors.C_BLUE_100} />
          </a>
        </div>

        <div className={cx('contentsArea')}>
          <div className={cx('contentsWrap')}>
            <Text className={cx('title')} fontStyle={FontStyle.B3P_B} color={Colors.C_COOL_GRAY_90}>
              작성자
            </Text>
            <Text fontStyle={FontStyle.B3P_M} color={Colors.C_COOL_GRAY_90}>
              {username}
            </Text>
          </div>
          <div className={cx('contentsWrap')}>
            <Text className={cx('title')} fontStyle={FontStyle.B3P_B} color={Colors.C_COOL_GRAY_90}>
              내용
            </Text>
            <Text fontStyle={FontStyle.B3P_M} color={Colors.C_COOL_GRAY_90}>
              {text}
            </Text>
          </div>
        </div>

        <div className={cx('reportArea')}>
          <Text fontStyle={FontStyle.B1_B} color={Colors.C_JOBDA_BLACK}>
            신고 사유
          </Text>
          <div className={cx('listArea')}>{getEls()}</div>
        </div>

        <Button disabled={getIsDisabled()} type={'submit'} className={cx('btnReport')}>
          신고하기
        </Button>

        <CloseButton className={cx('btnClose')} onClick={onClose} />
      </form>
    </Modal>
  );
};

export default ChatReportModal;
